import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { useAccountOwnerData } from '../../../hooks/useAccountOwnerData';
import {
  applyDateFilter,
  buildOverrideMap,
  applyOverrides,
  computePeriodRevenueByClient,
  computeEffectiveRateByClient,
  buildDrillDown,
  computeKPIs,
  buildAMChartData,
  deriveFYOptions,
  deriveCalendarYears,
  type PeriodType,
  type EffectiveRecord,
} from '../../../utils/accountOwnerUtils';
import { KpiStrip } from './KpiStrip';
import { ChartsSection } from './ChartsSection';
import { DrillDownTable } from './DrillDownTable';
import { AnalyticsChartsSection } from './AnalyticsChartsSection';

// ─── Skeleton loader ──────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 animate-pulse">
    <div className="h-2 w-20 bg-slate-200 rounded mb-3" />
    <div className="h-6 w-28 bg-slate-200 rounded" />
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="p-6 space-y-4">
    <div className="grid grid-cols-4 gap-2">
      {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
    <div className="bg-white rounded-xl border border-slate-200 shadow-md h-48 animate-pulse" />
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-md h-64 animate-pulse" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-md h-64 animate-pulse" />
    </div>
  </div>
);

// ─── Month names ──────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const FY_QUARTERS = [
  { value: 'all', label: 'All Quarters' },
  { value: 'Q1', label: 'Q1 (Apr–Jun)' },
  { value: 'Q2', label: 'Q2 (Jul–Sep)' },
  { value: 'Q3', label: 'Q3 (Oct–Dec)' },
  { value: 'Q4', label: 'Q4 (Jan–Mar)' },
];

// ─── Get last quarter based on current date (Indian FY) ──────────────────────────
// Returns { quarter, needsPreviousFY }
// Indian FY: Apr(4)-Jun(6)=Q1, Jul(7)-Sep(9)=Q2, Oct(10)-Dec(12)=Q3, Jan(1)-Mar(3)=Q4
const getLastQuarterInfo = (): { quarter: string; needsPreviousFY: boolean } => {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-12

  if (month >= 4 && month <= 6) {
    // Currently Q1 → last completed is Q4 of PREVIOUS FY
    return { quarter: 'Q4', needsPreviousFY: true };
  }
  if (month >= 7 && month <= 9) {
    // Currently Q2 → last completed is Q1 of CURRENT FY
    return { quarter: 'Q1', needsPreviousFY: false };
  }
  if (month >= 10 && month <= 12) {
    // Currently Q3 → last completed is Q2 of CURRENT FY
    return { quarter: 'Q2', needsPreviousFY: false };
  }
  // Currently Q4 (Jan-Mar) → last completed is Q3 of CURRENT FY
  return { quarter: 'Q3', needsPreviousFY: false };
};

// ─── Select helper ────────────────────────────────────────────────────────────
const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="flex items-center gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">{label}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

// ─── Period type toggle ───────────────────────────────────────────────────────
const PeriodToggle: React.FC<{
  value: PeriodType;
  onChange: (v: PeriodType) => void;
}> = ({ value, onChange }) => (
  <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
    {([['fy_india', 'FY India'], ['calendar', 'Calendar']] as [PeriodType, string][]).map(([pt, label]) => (
      <button
        key={pt}
        onClick={() => onChange(pt)}
        className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
          value === pt
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-slate-600 hover:text-slate-800'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

// ─── Multi-year dropdown ──────────────────────────────────────────────────────
const MultiYearDropdown: React.FC<{
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
  minWidth?: string;
}> = ({ label, options, selected, onChange, minWidth = '11rem' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const btnLabel = selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const toggle = (val: string) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        {btnLabel}
        <svg className={`w-3 h-3 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto" style={{ minWidth, maxHeight: '240px' }}>
          <label className="flex items-center gap-2 px-3 py-1.5 text-[12px] cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="accent-blue-600" checked={selected.length === 0}
              onChange={() => onChange([])} readOnly={selected.length === 0} />
            <span className="text-slate-600 font-medium">All</span>
          </label>
          <div className="border-t border-slate-100 my-1" />
          {options.map(o => (
            <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-[12px] cursor-pointer hover:bg-slate-50">
              <input type="checkbox" className="accent-blue-600" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              <span className="text-slate-700">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const AccountOwnerPortfolioPage: React.FC = () => {
  const {
    records,
    amManifest,
    projectToClient,
    clientBillingRateByClientId,
    gradeTechRateByGradeTech,
    clientInvoiceByPeriod,
    accountOwnerProjectOverrides,
    rateConfig,
    miscExpensePerHour,
    loading,
    error,
  } = useAccountOwnerData();

  // ── Filter state ────────────────────────────────────────────────────────────
  const [periodType, setPeriodType] = useState<PeriodType>('fy_india');
  const [fyYears, setFyYears] = useState<string[]>([]);
  const [fyQuarter, setFyQuarter] = useState<string>('all');
  const [tsYears, setTsYears] = useState<string[]>([]);
  const [tsMonth, setTsMonth] = useState<string>('all');
  const [selectedAMs, setSelectedAMs] = useState<string[]>([]);
  const [excludeTax, setExcludeTax] = useState<boolean>(true);
  const [sortKey, setSortKey] = useState<string>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  /** One-time defaults after data loads; do not fight user "All" (empty selection). */
  const fyFilterBootstrappedRef = useRef(false);
  const calendarFilterBootstrappedRef = useRef(false);

  // ── Derive FY / Calendar year options ───────────────────────────────────────
  const fyOptions = useMemo(() => deriveFYOptions(records), [records]);
  const calendarYears = useMemo(() => deriveCalendarYears(records), [records]);

  // ── FY India: once when options exist, default FY + last completed quarter ──
  useEffect(() => {
    if (periodType !== 'fy_india' || fyOptions.length === 0 || fyFilterBootstrappedRef.current) {
      return;
    }
    fyFilterBootstrappedRef.current = true;
    const { quarter, needsPreviousFY } = getLastQuarterInfo();
    if (needsPreviousFY && fyOptions.length > 1) {
      setFyYears([fyOptions[1]]);
      setFyQuarter(quarter);
    } else {
      setFyYears([fyOptions[0]]);
      setFyQuarter(quarter);
    }
  }, [periodType, fyOptions]);

  // ── Calendar: once when options exist, default to latest calendar year ───────
  useEffect(() => {
    if (periodType !== 'calendar' || calendarYears.length === 0 || calendarFilterBootstrappedRef.current) {
      return;
    }
    calendarFilterBootstrappedRef.current = true;
    setTsYears([calendarYears[0]]);
  }, [periodType, calendarYears]);

  // ── Cascading resets ─────────────────────────────────────────────────────────
  const handleFyYearsChange = (v: string[]) => {
    setFyYears(v);
    setFyQuarter('all');
  };

  const handlePeriodTypeChange = (v: PeriodType) => {
    setPeriodType(v);
    setFyYears([]);
    setTsYears([]);
    setFyQuarter('all');
    setTsMonth('all');
    fyFilterBootstrappedRef.current = false;
    calendarFilterBootstrappedRef.current = false;
  };

  // ── All AM names for filter pills ────────────────────────────────────────────
  const allAMNames = useMemo(() => {
    return amManifest.map(am => am.name).sort();
  }, [amManifest]);

  // ── Override map ─────────────────────────────────────────────────────────────
  const overrideMap = useMemo(
    () => buildOverrideMap(accountOwnerProjectOverrides, amManifest),
    [accountOwnerProjectOverrides, amManifest]
  );

  // ── Apply overrides ──────────────────────────────────────────────────────────
  const effectiveRecords = useMemo(
    () => applyOverrides(records, overrideMap),
    [records, overrideMap]
  );

  // ── Date filter ──────────────────────────────────────────────────────────────
  const dateFiltered = useMemo(
    (): EffectiveRecord[] =>
      applyDateFilter(effectiveRecords, periodType, fyYears, fyQuarter, tsYears, tsMonth),
    [effectiveRecords, periodType, fyYears, fyQuarter, tsYears, tsMonth]
  );

  // ── AM filter ────────────────────────────────────────────────────────────────
  const amFiltered = useMemo(() => {
    if (selectedAMs.length === 0) return dateFiltered;
    const amSet = new Set(selectedAMs);
    return dateFiltered.filter(r => amSet.has(r.effective_account_manager_name));
  }, [dateFiltered, selectedAMs]);

  // ── Period revenue by client ─────────────────────────────────────────────────
  const periodRevenueByClient = useMemo(
    () => computePeriodRevenueByClient(
      clientInvoiceByPeriod,
      periodType,
      fyYears,
      fyQuarter,
      tsYears,
      tsMonth,
      excludeTax
    ),
    [clientInvoiceByPeriod, periodType, fyYears, fyQuarter, tsYears, tsMonth, excludeTax]
  );

  // ── Effective billing rate per client ────────────────────────────────────────
  const effectiveRateByClient = useMemo(
    () => computeEffectiveRateByClient(amFiltered, projectToClient, periodRevenueByClient),
    [amFiltered, projectToClient, periodRevenueByClient]
  );

  // ── Drill-down hierarchy ─────────────────────────────────────────────────────
  const drillDown = useMemo(
    () => buildDrillDown(
      amFiltered,
      projectToClient,
      clientBillingRateByClientId,
      effectiveRateByClient,
      gradeTechRateByGradeTech,
      rateConfig,
      miscExpensePerHour
    ),
    [amFiltered, projectToClient, clientBillingRateByClientId, effectiveRateByClient, gradeTechRateByGradeTech, rateConfig, miscExpensePerHour]
  );

  // ── KPIs ─────────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => computeKPIs(drillDown), [drillDown]);

  // ── Chart data ───────────────────────────────────────────────────────────────
  const chartData = useMemo(() => buildAMChartData(drillDown), [drillDown]);

  // ── Sort handler ─────────────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // ── Tax toggle label ─────────────────────────────────────────────────────────
  const taxLabel = excludeTax ? 'Ex. Tax' : 'With Tax';

  // ── Calendar month options ────────────────────────────────────────────────────
  const calendarMonthOptions = useMemo(() => {
    const opts = [{ value: 'all', label: 'All Months' }];
    if (tsYears.length === 0) return opts;
    const monthsInYears = new Set(
      effectiveRecords
        .filter(r => tsYears.includes(String(r.year)))
        .map(r => String(r.month))
    );
    MONTH_NAMES.forEach(m => {
      if (monthsInYears.has(m.value)) opts.push(m);
    });
    return opts;
  }, [effectiveRecords, tsYears]);

  // ── Render ────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <AppLayout>
        <PageHeader title="Account Owner Portfolio" />
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-1">Failed to load data</p>
            <p className="text-slate-600 text-sm">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Account Owner Portfolio"
        filters={
          <div className="flex items-center gap-2 flex-wrap">
            {/* Period type toggle */}
            <PeriodToggle value={periodType} onChange={handlePeriodTypeChange} />

            <div className="h-4 w-px bg-slate-200" />

            {/* FY India filters */}
            {periodType === 'fy_india' && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">FY</span>
                  <MultiYearDropdown
                    label="FY Year"
                    options={fyOptions.map(y => ({ value: y, label: `FY ${y}–${String(Number(y) + 1).slice(2)}` }))}
                    selected={fyYears}
                    onChange={handleFyYearsChange}
                  />
                </div>
                <div className="h-4 w-px bg-slate-200" />
                <FilterSelect
                  label="Q"
                  value={fyQuarter}
                  onChange={setFyQuarter}
                  options={FY_QUARTERS}
                />
              </>
            )}

            {/* Calendar filters */}
            {periodType === 'calendar' && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Year</span>
                  <MultiYearDropdown
                    label="Year"
                    options={calendarYears.map(y => ({ value: y, label: y }))}
                    selected={tsYears}
                    onChange={v => { setTsYears(v); setTsMonth('all'); }}
                  />
                </div>
                <div className="h-4 w-px bg-slate-200" />
                <FilterSelect
                  label="Month"
                  value={tsMonth}
                  onChange={setTsMonth}
                  options={calendarMonthOptions}
                />
              </>
            )}

            <div className="h-4 w-px bg-slate-200" />

            {allAMNames.length > 0 && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
                    Account Owner
                  </span>
                  <MultiYearDropdown
                    label="All account owners"
                    options={allAMNames.map(name => ({ value: name, label: name }))}
                    selected={selectedAMs}
                    onChange={setSelectedAMs}
                    minWidth="14rem"
                  />
                </div>
                <div className="h-4 w-px bg-slate-200" />
              </>
            )}

            <button
              type="button"
              onClick={() => setExcludeTax(v => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition-colors ${
                excludeTax
                  ? 'bg-amber-50 text-amber-700 border-amber-300'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {taxLabel}
            </button>
          </div>
        }
      />

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="p-6 space-y-6">

          <KpiStrip kpis={kpis} />

          {/* Full-width drill-down (primary); charts below — matches v2 light shell */}
          <div className="w-full min-w-0">
            <DrillDownTable
              drillDown={drillDown}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
            />
          </div>

          <div className="w-full min-w-0">
            <ChartsSection chartData={chartData} />
          </div>

          {/* Monthly analytics charts — uses the same FY selected above */}
          {periodType === 'fy_india' && fyYears.length > 0 && (
            <div className="w-full min-w-0">
              <AnalyticsChartsSection
                effectiveRecords={effectiveRecords}
                gradeTechRates={gradeTechRateByGradeTech}
                rateConfig={rateConfig}
                clientBillingRateByClientId={clientBillingRateByClientId}
                projectToClient={projectToClient}
                effectiveRateByClient={effectiveRateByClient}
                fyYear={fyYears[0]}
              />
            </div>
          )}

        </div>
      )}
    </AppLayout>
  );
};

export default AccountOwnerPortfolioPage;
